import { createContext, useCallback, useContext, useState } from 'react'

import type { AppTypeRequest } from '~/routes/api/app-type'

type AppType = AppTypeRequest['appType']

type Options = {
  logoutTimeout?: number
  appType?: AppType
  isQuickDemoEnabled?: boolean
}

export type EnvDefaults = {
  appType: NonNullable<AppType>
  logoutTimeout: number
  isQuickDemoEnabled: boolean
}

type AppTypeContextType = {
  isDev: boolean
  appType: AppType
  logoutTimeout: number
  isQuickDemoEnabled: boolean
  envDefaults: EnvDefaults
  setOptions: (options: Options) => void
}

export const AppTypeContext = createContext<AppTypeContextType | null>(null)

export const AppTypeProvider = ({
  children,
  options
}: {
  children: React.ReactNode
  options: {
    defaultLogoutTimeout: number
    defaultIsQuickDemoEnabled: boolean
    isDev: boolean
    defaultAppType: AppType
    envDefaults: EnvDefaults
  }
}) => {
  const [appType, setAppType] = useState<AppType>(options.defaultAppType)
  const [logoutTimeout, setLogoutTimeout] = useState<number>(
    options.defaultLogoutTimeout
  )
  const [isQuickDemoEnabled, setIsQuickDemoEnabled] = useState<boolean>(
    options.defaultIsQuickDemoEnabled
  )

  const setOptions = useCallback((options: Options) => {
    if (options.appType) setAppType(options.appType)

    if (options.logoutTimeout) setLogoutTimeout(options.logoutTimeout)

    if (options.isQuickDemoEnabled !== undefined)
      setIsQuickDemoEnabled(options.isQuickDemoEnabled)
  }, [])

  return (
    <AppTypeContext.Provider
      value={{
        isDev: options.isDev,
        appType,
        logoutTimeout,
        isQuickDemoEnabled,
        envDefaults: options.envDefaults,
        setOptions
      }}
    >
      {children}
    </AppTypeContext.Provider>
  )
}

export const useAppType = () => {
  const context = useContext(AppTypeContext)

  if (!context) {
    throw new Error('useAppType must be used within a AppTypeProvider')
  }

  return context
}
